import { useState, useCallback, useEffect } from "react"
import { DisplayMessage } from "../ZeefTypes"

interface UseZeefKeyboardShortcutsReturn {
  // State
  showMessageIdentifiers: boolean
  messageIdentifierMap: Map<string, string>
  typedIdentifierSequence: string
  filteredIdentifierMap: Map<string, string>

  // Actions
  generateMessageIdentifiers: (messages: DisplayMessage[]) => void
  showIdentifiers: () => void
  hideIdentifiers: () => void
  handleIdentifierKeyPress: (key: string, messages: DisplayMessage[], onReplyToMessage: (message: DisplayMessage) => void) => void
  handleIdentifierBackspace: () => void
  handleGlobalKeyDown: (
    event: KeyboardEvent,
    isVisible: boolean,
    messageInputRef: React.RefObject<HTMLInputElement>,
    replyingToMessage: any,
    onCancelReply: () => void
  ) => void
  handleGlobalClick: (event: MouseEvent) => void
  handleWidgetClick: (event: MouseEvent) => void
}

export const useZeefKeyboardShortcuts = (): UseZeefKeyboardShortcutsReturn => {
  const [showMessageIdentifiers, setShowMessageIdentifiers] = useState(false)
  const [messageIdentifierMap, setMessageIdentifierMap] = useState<Map<string, string>>(new Map())
  const [typedIdentifierSequence, setTypedIdentifierSequence] = useState("")
  const [filteredIdentifierMap, setFilteredIdentifierMap] = useState<Map<string, string>>(new Map())

  const generateIdentifier = useCallback((index: number): string => {
    const letters = ["e", "a", "r", "s", "t", "n", "i", "o", "p"]
    if (index < letters.length) {
      return letters[index]
    }
    const firstLetterIndex = Math.floor((index - letters.length) / letters.length)
    const secondLetterIndex = (index - letters.length) % letters.length
    return letters[firstLetterIndex] + letters[secondLetterIndex]
  }, [])

  const generateMessageIdentifiers = useCallback((messages: DisplayMessage[]) => {
    const identifierMap = new Map<string, string>()
    
    messages.forEach((message, index) => {
      const identifier = generateIdentifier(index)
      identifierMap.set(message.id, identifier)
    })

    setMessageIdentifierMap(identifierMap)
    setFilteredIdentifierMap(new Map(identifierMap))
  }, [generateIdentifier])

  const showIdentifiers = useCallback(() => {
    setShowMessageIdentifiers(true)
  }, [])

  const hideIdentifiers = useCallback(() => {
    setShowMessageIdentifiers(false)
    setMessageIdentifierMap(new Map())
    setTypedIdentifierSequence("")
    setFilteredIdentifierMap(new Map())
  }, [])

  const filterIdentifiers = useCallback((sequence: string, messages: DisplayMessage[], onReplyToMessage: (message: DisplayMessage) => void) => {
    if (sequence === "") {
      setFilteredIdentifierMap(new Map(messageIdentifierMap))
      return
    }

    const filteredMap = new Map<string, string>()
    for (const [messageId, identifier] of messageIdentifierMap.entries()) {
      if (identifier.startsWith(sequence)) {
        filteredMap.set(messageId, identifier)
      }
    }

    setFilteredIdentifierMap(filteredMap)

    // If only one candidate remains, automatically select it for reply
    if (filteredMap.size === 1) {
      const [selectedMessageId] = filteredMap.keys()
      const selectedMessage = messages.find((msg) => msg.id === selectedMessageId)

      if (selectedMessage) {
        hideIdentifiers()
        onReplyToMessage(selectedMessage)
      }
    }
  }, [messageIdentifierMap, hideIdentifiers])

  const handleIdentifierKeyPress = useCallback((key: string, messages: DisplayMessage[], onReplyToMessage: (message: DisplayMessage) => void) => {
    const newSequence = typedIdentifierSequence + key
    setTypedIdentifierSequence(newSequence)
    filterIdentifiers(newSequence, messages, onReplyToMessage)
  }, [typedIdentifierSequence, filterIdentifiers])

  const handleIdentifierBackspace = useCallback(() => {
    if (typedIdentifierSequence.length > 0) {
      const newSequence = typedIdentifierSequence.slice(0, -1)
      setTypedIdentifierSequence(newSequence)
      
      if (newSequence === "") {
        setFilteredIdentifierMap(new Map(messageIdentifierMap))
      } else {
        const filteredMap = new Map<string, string>()
        for (const [messageId, identifier] of messageIdentifierMap.entries()) {
          if (identifier.startsWith(newSequence)) {
            filteredMap.set(messageId, identifier)
          }
        }
        setFilteredIdentifierMap(filteredMap)
      }
    }
  }, [typedIdentifierSequence, messageIdentifierMap])

  const handleGlobalKeyDown = useCallback((
    event: KeyboardEvent,
    isVisible: boolean,
    messageInputRef: React.RefObject<HTMLInputElement>,
    replyingToMessage: any,
    onCancelReply: () => void
  ) => {
    // Only handle keyboard shortcuts when the chat is visible
    if (!isVisible) {
      return
    }

    const isMessageInputFocused = document.activeElement === messageInputRef.current
    const isUsernameInputFocused = document.activeElement && (document.activeElement as HTMLElement).classList.contains("username-input")
    const isSearchInputFocused = document.activeElement && (document.activeElement as HTMLElement).classList.contains("user-search-input")

    // Handle escape key specially
    if (event.key === "Escape") {
      if (isMessageInputFocused) {
        event.preventDefault()
        event.stopPropagation()
        messageInputRef.current?.blur()
        return
      } else if (isUsernameInputFocused) {
        event.preventDefault()
        event.stopPropagation()
        const activeElement = document.activeElement as HTMLElement
        if (activeElement && activeElement.blur) {
          activeElement.blur()
        }
        return
      } else if (isSearchInputFocused) {
        event.preventDefault()
        event.stopPropagation()
        const activeElement = document.activeElement as HTMLElement
        if (activeElement && activeElement.blur) {
          activeElement.blur()
        }
        return
      } else if (replyingToMessage) {
        event.preventDefault()
        event.stopPropagation()
        onCancelReply()
        return
      } else if (showMessageIdentifiers) {
        event.preventDefault()
        event.stopPropagation()
        hideIdentifiers()
        return
      }
    }

    // Only handle other keyboard shortcuts when inputs are not focused
    if (isMessageInputFocused || isUsernameInputFocused || isSearchInputFocused) {
      return
    }

    if (event.key === "r" && !showMessageIdentifiers) {
      event.preventDefault()
      showIdentifiers()
    } else if (showMessageIdentifiers) {
      // Handle identifier filtering when identifiers are shown
      if (event.key === "Backspace") {
        event.preventDefault()
        handleIdentifierBackspace()
      } else if (event.key.length === 1 && /[a-z]/i.test(event.key)) {
        // Only handle single letter keys
        event.preventDefault()
        // This would need messages and onReplyToMessage from parent
        // handleIdentifierKeyPress(event.key.toLowerCase(), messages, onReplyToMessage)
      }
    }
  }, [showMessageIdentifiers, hideIdentifiers, showIdentifiers, handleIdentifierBackspace])

  const handleGlobalClick = useCallback((event: MouseEvent) => {
    // Hide identifiers when clicking anywhere, but not when clicking on input elements
    if (showMessageIdentifiers) {
      const target = event.target as Element
      if (
        !target.matches("input, textarea, button, select, [contenteditable]") &&
        !target.closest("input, textarea, button, select, [contenteditable]")
      ) {
        hideIdentifiers()
      }
    }
  }, [showMessageIdentifiers, hideIdentifiers])

  const handleWidgetClick = useCallback((event: MouseEvent) => {
    // Handle clicks within the zeef chat widget
    if (showMessageIdentifiers) {
      const target = event.target as Element
      if (
        !target.matches(
          "input, textarea, button, select, [contenteditable], .clickable-username, .send-btn, .mode-toggle-btn, .cancel-reply-btn"
        ) &&
        !target.closest(
          "input, textarea, button, select, [contenteditable], .clickable-username, .send-btn, .mode-toggle-btn, .cancel-reply-btn"
        )
      ) {
        hideIdentifiers()
      }
    }
  }, [showMessageIdentifiers, hideIdentifiers])

  // Set up global event listeners
  useEffect(() => {
    const keyDownHandler = (event: KeyboardEvent) => {
      // This will be called by the parent component with the right parameters
    }

    const clickHandler = (event: MouseEvent) => {
      handleGlobalClick(event)
    }

    document.addEventListener("keydown", keyDownHandler)
    document.addEventListener("click", clickHandler)

    return () => {
      document.removeEventListener("keydown", keyDownHandler)
      document.removeEventListener("click", clickHandler)
    }
  }, [handleGlobalClick])

  return {
    // State
    showMessageIdentifiers,
    messageIdentifierMap,
    typedIdentifierSequence,
    filteredIdentifierMap,

    // Actions
    generateMessageIdentifiers,
    showIdentifiers,
    hideIdentifiers,
    handleIdentifierKeyPress,
    handleIdentifierBackspace,
    handleGlobalKeyDown,
    handleGlobalClick,
    handleWidgetClick,
  }
}
